import { useState } from 'react';
import { cn } from '../../lib/cn';
import { Card } from './Card';
import { Badge } from './Badge';
import { Button } from './Button';
import { AIRecommendationCard } from './AIRecommendationCard';
import type { AIXRecommendation } from '../../aix/framework';

type Props = {
  recommendations?: AIXRecommendation[];
  contextSummary?: string;
  className?: string;
};

export function CopilotPanel({ recommendations = [], contextSummary, className }: Props) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; text: string }[]>([
    { role: 'assistant', text: 'I have assembled your workspace context. How can I help?' },
  ]);

  const handleSend = () => {
    if (!input.trim()) return;
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: input },
      { role: 'assistant', text: 'Analyzing with your current context… (demo mode)' },
    ]);
    setInput('');
  };

  return (
    <Card className={cn('flex h-full flex-col', className)}>
      <div className="flex items-center justify-between border-b border-slate-200 pb-3 dark:border-slate-700">
        <div>
          <div className="flex items-center gap-2">
            <Badge variant="ai">Copilot</Badge>
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">Workspace AI</span>
          </div>
          {contextSummary && (
            <p className="mt-1 text-xs text-slate-500">{contextSummary}</p>
          )}
        </div>
      </div>

      {recommendations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Proactive Insights</p>
          {recommendations.slice(0, 2).map((rec) => (
            <AIRecommendationCard
              key={rec.id}
              title={rec.title}
              description={rec.explanation.what}
              confidence={rec.explanation.confidence}
              expectedImpact={rec.explanation.expected_impact}
              requiresApproval={rec.requires_approval}
              type={rec.category}
            />
          ))}
        </div>
      )}

      <div className="mt-3 flex-1 space-y-2 overflow-y-auto">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={cn(
              'rounded-lg px-3 py-2 text-sm',
              msg.role === 'user'
                ? 'ml-8 bg-brand-50 text-brand-900 dark:bg-brand-950 dark:text-brand-100'
                : 'mr-8 bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            {msg.text}
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2 border-t border-slate-200 pt-3 dark:border-slate-700">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Ask anything about this workspace…"
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          aria-label="Copilot message input"
        />
        <Button size="sm" onClick={handleSend}>Send</Button>
      </div>
    </Card>
  );
}
